/**
 * Business Profile vocabulary (`/business-profile/:tab`): identity, offers,
 * audience, sources and confirmation states. Assembled into `WorkspaceCopy` by
 * `../copy.ts`.
 */

export const profileCopyEn = {
  profile: {
    title: 'Business Profile',
    intro: (ws: string) =>
      `What Mouja remembers about ${ws}. Every Production Line in this workspace starts from here.`,
    sections: 'Business Profile sections',
    business: 'Business',
    offers: 'Offers',
    taste: 'Creative Taste',
    sources: 'Sources',
    name: 'Name',
    description: 'Description',
    geography: 'Geography',
    website: 'Website',
    editDetails: 'Edit details',
    editTitle: 'Edit Business Profile',
    editBody: (ws: string) => `Shared by every Production Line in ${ws}.`,
    businessName: 'Business name',
    nameRequired: 'Business name is required.',
    save: 'Save changes',
    saving: 'Saving…',
    saved: 'Business Profile updated.',
    noOffers:
      'No offers yet. Add the concrete programmes, products or services Mouja should make videos about.',
    addOffer: 'Add offer',
    editOffer: 'Edit offer',
    saveOffer: 'Save offer',
    offerName: 'Offer name',
    offerNamePlaceholder: 'e.g. Signature coffee subscription',
    offerNameRequired: 'Give the offer a name.',
    offerDesc: 'Short description',
    offerDescPlaceholder: 'Who it is for and what they get',
    edit: 'Edit',
    remove: 'Remove',
    editAria: (name: string) => `Edit ${name}`,
    removeAria: (name: string) => `Remove ${name}`,
    removed: (name: string) => `Removed “${name}”.`,
    offerAdded: 'Offer added.',
    offerUpdated: 'Offer updated.',
    incompleteOffers: 'No offers added.',
    incompleteOffersBody: 'Add a concrete offer so recommendations stay specific.',
    incompleteTaste: 'Creative taste not calibrated.',
    incompleteTasteBody:
      'Recommendations use a neutral style until calibration is available.',
    tasteEmptyTitle: 'Creative taste not calibrated',
    tasteEmptyBody: (ws: string) =>
      `Three quick choices — polished or organic, bold or authoritative, fast-paced or calm — will shape every recommendation Mouja makes for ${ws}.`,
    calibrate: 'Calibrate creative taste',
    tasteRows: [
      { label: 'Polished or Organic', left: 'Polished', right: 'Organic' },
      { label: 'Bold or Authoritative', left: 'Bold', right: 'Authoritative' },
      { label: 'Fast-paced or Calm', left: 'Fast-paced', right: 'Calm' },
    ],
    tasteApplies: (ws: string) =>
      `These preferences will apply across future Production Lines in ${ws}. Any Production Line will be able to adjust them for itself without changing the workspace default.`,
    sourceRead: 'Read',
    sourceNotConnected: 'Not connected',
    refresh: 'Refresh',
    connect: 'Connect',
    refreshSoon: 'Refreshing a source is not available yet.',
    connectSoon:
      'Add an Instagram or TikTok profile when you start a new Production Line; connecting one here is coming soon.',
    sourcesReadOnly:
      'Sources are read-only. Mouja reads public information only and never posts on your behalf.',
    confirmed: 'Confirmed',
    readOnly: 'This profile is read-only while Mouja is checking it.',
    missing: 'No business profile found',
    missingBody: 'Start a Production Line from your website to build one.',
    correctionFailed: 'The change could not be saved. Nothing was lost — try again.',
    missingFieldsPrefix: 'Still needed:',
    websitePlatform: 'Website',
    instagram: 'Instagram',
    tiktok: 'TikTok',
  },
};

export const profileCopyAr: typeof profileCopyEn = {
  profile: {
    title: 'الملف التجاري',
    intro: (ws: string) =>
      `هذا اللي تتذكره موجة عن ${ws}. وكل خط إنتاج في مساحة العمل هذي يبدأ من هنا.`,
    sections: 'أقسام الملف التجاري',
    business: 'النشاط',
    offers: 'العروض',
    taste: 'الذوق الإبداعي',
    sources: 'المصادر',
    name: 'الاسم',
    description: 'الوصف',
    geography: 'الموقع الجغرافي',
    website: 'الموقع',
    editDetails: 'عدّل التفاصيل',
    editTitle: 'تعديل الملف التجاري',
    editBody: (ws: string) => `مشترك بين كل خطوط الإنتاج في ${ws}.`,
    businessName: 'اسم النشاط',
    nameRequired: 'اسم النشاط مطلوب.',
    save: 'احفظ التغييرات',
    saving: 'جاري الحفظ…',
    saved: 'انحدّث الملف التجاري.',
    noOffers:
      'ما فيه عروض لين الحين. ضيف البرامج أو المنتجات أو الخدمات اللي تبي موجة تسوي عنها فيديوهات.',
    addOffer: 'ضيف عرض',
    editOffer: 'تعديل العرض',
    saveOffer: 'احفظ العرض',
    offerName: 'اسم العرض',
    offerNamePlaceholder: 'مثال: اشتراك القهوة المميزة',
    offerNameRequired: 'عطِ العرض اسم.',
    offerDesc: 'وصف قصير',
    offerDescPlaceholder: 'لمين موجّه ووش يحصلون عليه',
    edit: 'تعديل',
    remove: 'شِيل',
    editAria: (name: string) => `تعديل ${name}`,
    removeAria: (name: string) => `شِيل ${name}`,
    removed: (name: string) => `انشال «${name}».`,
    offerAdded: 'انضاف العرض.',
    offerUpdated: 'انحدّث العرض.',
    incompleteOffers: 'ما فيه عروض مضافة.',
    incompleteOffersBody: 'ضيف عرض واضح عشان تبقى التوصيات محددة.',
    incompleteTaste: 'الذوق الإبداعي لسه ما تعاير.',
    incompleteTasteBody: 'التوصيات تستخدم أسلوب محايد لين تتوفر المعايرة.',
    tasteEmptyTitle: 'الذوق الإبداعي لسه ما تعاير',
    tasteEmptyBody: (ws: string) =>
      `ثلاثة خيارات سريعة — مصقول أو طبيعي، جريء أو موثوق، سريع أو هادئ — راح تشكّل كل توصية تقدمها موجة لـ ${ws}.`,
    calibrate: 'عاير الذوق الإبداعي',
    tasteRows: [
      { label: 'مصقول أم طبيعي', left: 'مصقول', right: 'طبيعي' },
      { label: 'جريء أم موثوق', left: 'جريء', right: 'موثوق' },
      { label: 'سريع أم هادئ', left: 'سريع', right: 'هادئ' },
    ],
    tasteApplies: (ws: string) =>
      `هذي التفضيلات راح تنطبق على خطوط الإنتاج الجاية في ${ws}. وأي خط إنتاج يقدر يعدّلها لنفسه بدون ما يغيّر الإعداد الافتراضي.`,
    sourceRead: 'انقرأ',
    sourceNotConnected: 'مو مربوط',
    refresh: 'حدّث',
    connect: 'اربط',
    refreshSoon: 'تحديث المصدر مو متاح لين الحين.',
    connectSoon:
      'ضيف ملف Instagram أو TikTok لما تبدأ خط إنتاج جديد؛ وربطه من هنا جاي قريب.',
    sourcesReadOnly:
      'المصادر للقراءة بس. موجة تقرأ المعلومات العامة بس وما تنشر نيابة عنك أبدًا.',
    confirmed: 'مؤكد',
    readOnly: 'هذا الملف للقراءة بس، لأن موجة تتأكد منه الحين.',
    missing: 'ما لقينا ملف تجاري',
    missingBody: 'ابدأ خط إنتاج من موقعك عشان نسوي لك واحد.',
    correctionFailed: 'ما قدرنا نحفظ التغيير. ما ضاع شي — جرّب مرة ثانية.',
    missingFieldsPrefix: 'لسه مطلوب:',
    websitePlatform: 'الموقع',
    instagram: 'Instagram',
    tiktok: 'TikTok',
  },
};
